import { NgTemplateOutlet } from '@angular/common';
import {
    computed,
    effect,
    input,
    viewChild,
    Component,
    linkedSignal,
    contentChildren,
    TemplateRef,
    ChangeDetectionStrategy,
} from '@angular/core';

/**
 * interfaces
 */
import { ITableColumnsUnion, ITableTreeNode, ITableTreeRowNode } from '../../interfaces';

/**
 * services
 */
import { TableStateService } from '../../services/table-state.service';

/**
 * directives
 */
import { TemplateDirective } from '@sharedDirectives/templates/template.directive';

/**
 * components
 */
import { TableComponent } from '../table';

/**
 * A hierarchical table: it takes a tree, keeps which rows are expanded, and
 * hands the currently visible rows — depth-first, in input order — to an
 * inner `app-table`, which does all the rendering and the loading, error and
 * empty states.
 *
 * Templates: `appTemplate="header"` gets `$implicit: columns`;
 * `appTemplate="body"` gets `$implicit: rowNode` (an {@link ITableTreeRowNode}),
 * `rowData` and `columns`.
 *
 * The table uses a fixed layout: the columns split the width equally unless a
 * header cell sets its own width, and opening rows never moves them.
 *
 * Sorting, search, paging and selection are not supported: they would act on
 * the flattened rows and break the hierarchy.
 */
@Component({
    selector: 'app-tree-table',
    templateUrl: './tree-table.component.html',
    styleUrl: './tree-table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, TableComponent, TemplateDirective],
})
export class TreeTableComponent<T> {

    /** The roots of the tree. A new array resets the expansion to {@link initialExpandedDepth}. */
    public readonly value = input<ITableTreeNode<T>[]>([]);

    /**
     * How many levels start expanded: 0 shows the roots collapsed, 1 opens the
     * roots so their children show (collapsed), and so on.
     */
    public readonly initialExpandedDepth = input<number>(0);

    /** Gives the loading, error and empty rows their full colspan, and the templates their `columns`. */
    public readonly columns = input<ITableColumnsUnion>([]);

    public readonly loading = input<boolean>(false);

    /** The message the error row shows, or `null` when there is no error. */
    public readonly error = input<string | null>(null);

    public readonly tableClassStyle = input<string | string[]>('');

    /**
     * Fixed layout always: under auto layout the hierarchy cell, which widens
     * with depth, would move every column each time a deeper row opens.
     */
    protected readonly tableClasses = computed(() => ['table_layout_fixed', ...[this.tableClassStyle()].flat()]);

    private readonly _templates = contentChildren(TemplateDirective);

    protected readonly headerTemplate = computed(() => this._findTemplate('header'));

    protected readonly bodyTemplate = computed(() => this._findTemplate('body'));

    /** Only parent ids are kept. A new tree or depth recomputes the set from scratch. */
    private readonly _expandedIds = linkedSignal<Set<string>>(() =>
        this._collectInitiallyExpandedIds(this.value(), this.initialExpandedDepth()),
    );

    protected readonly visibleRows = computed<ITableTreeRowNode<T>[]>(() => {

        const expandedIds = this._expandedIds();
        const rows: ITableTreeRowNode<T>[] = [];

        const appendVisible = (nodes: ITableTreeNode<T>[], depth: number): void => {
            for (const node of nodes) {
                const hasChildren = (node.children?.length ?? 0) > 0;
                const isExpanded = hasChildren && expandedIds.has(node.id);

                rows.push({ id: node.id, data: node.data, depth, hasChildren, isExpanded });

                if (isExpanded) appendVisible(node.children!, depth + 1);
            }
        };

        appendVisible(this.value(), 0);

        return rows;
    });

    private readonly _tableState = viewChild(TableComponent, { read: TableStateService });

    constructor() {

        // the inner table takes its loading and error states through its state service only
        effect(() => {
            const tableState = this._tableState();
            if (!tableState) return;

            tableState.setLoading(this.loading());

            // `setError` refuses null; an empty message is the table's "no error"
            tableState.setError(this.error() ?? '');
        });
    }

    /**
     * Expands or collapses one row. Its descendants keep their own state, so
     * re-expanding a row shows them as they were left.
     */
    public toggleRow(rowNode: ITableTreeRowNode<unknown>): void {

        if (!rowNode.hasChildren) return;

        this._expandedIds.update((ids) => {
            const nextIds = new Set(ids);

            if (!nextIds.delete(rowNode.id)) nextIds.add(rowNode.id);

            return nextIds;
        });
    }

    private _findTemplate(name: 'header' | 'body'): TemplateRef<unknown> | null {
        return this._templates().find((template) => template.name === name)?.templateRef ?? null;
    }

    private _collectInitiallyExpandedIds(roots: ITableTreeNode<T>[], expandedDepth: number): Set<string> {

        const ids = new Set<string>();

        const collect = (nodes: ITableTreeNode<T>[], depth: number): void => {
            if (depth >= expandedDepth) return;

            for (const node of nodes) {
                if (!node.children?.length) continue;

                ids.add(node.id);
                collect(node.children, depth + 1);
            }
        };

        collect(roots, 0);

        return ids;
    }
}
